/**
 * ChartContainer — Universal chart wrapper that renders any Recharts chart
 * type from a declarative config object.
 *
 * Supports: Line, Bar, Area, Pie, Donut, Composed (mixed), Stacked.
 *
 * Features:
 * - Config-driven (no JSX chart composition needed)
 * - Consistent theming via chartTheme
 * - Responsive (ResponsiveContainer)
 * - Loading + empty states
 * - Automatic legend + tooltip
 * - Indian locale number formatting
 *
 * Usage:
 * ```tsx
 * <ChartContainer
 *   type="bar"
 *   data={[{ month: 'Jan', revenue: 50000, expenses: 30000 }]}
 *   series={[
 *     { dataKey: 'revenue', name: 'Revenue', color: '#2563eb' },
 *     { dataKey: 'expenses', name: 'Expenses', color: '#dc2626' },
 *   ]}
 *   xAxisKey="month"
 *   height={300}
 * />
 * ```
 */

import * as React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';
import {
  chartTheme,
  getChartColor,
  defaultCartesianGridProps,
  defaultXAxisProps,
  defaultYAxisProps,
  defaultTooltipProps,
  defaultLegendProps,
} from './chart-theme';
import { getTickFormatter, type TickFormatterType } from './chart-formatters';

// ─── Types ─────────────────────────────────────────────────────────

export type ChartType = 'line' | 'bar' | 'area' | 'pie' | 'donut' | 'composed';

export interface ChartSeries {
  /** Data key in the data array */
  dataKey: string;
  /** Display name for legend/tooltip */
  name?: string;
  /** Color override (defaults to chartColors palette) */
  color?: string;
  /** For composed charts: which chart type for this series */
  chartType?: 'line' | 'bar' | 'area';
  /** Stack ID for stacked charts */
  stackId?: string;
  /** Whether area should be filled (for area charts) */
  fill?: boolean;
  /** Hide from chart (keep in data) */
  hidden?: boolean;
}

export interface ChartContainerProps {
  /** Chart type to render */
  type: ChartType;
  /** Data array (records with numeric values) */
  data: Record<string, unknown>[];
  /** Series configuration */
  series: ChartSeries[];
  /** X-axis data key (for Cartesian charts) */
  xAxisKey?: string;
  /** Chart height in px */
  height?: number;
  /** Chart width (usually "100%") */
  width?: string | number;
  /** Show legend */
  showLegend?: boolean;
  /** Show tooltip */
  showTooltip?: boolean;
  /** Show grid */
  showGrid?: boolean;
  /** X-axis tick formatter type */
  xAxisFormat?: TickFormatterType;
  /** Y-axis tick formatter type */
  yAxisFormat?: TickFormatterType;
  /** Tooltip value formatter type */
  tooltipFormat?: TickFormatterType;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Additional class name */
  className?: string;
  /** Inner radius for donut charts (0-1) */
  innerRadius?: number;
  /** Outer radius for pie/donut charts */
  outerRadius?: number;
  /** Pie label key */
  pieLabelKey?: string;
  /** Pie value key */
  pieValueKey?: string;
}

// ─── Component ──────────────────────────────────────────────────────

export function ChartContainer({
  type,
  data,
  series,
  xAxisKey = 'name',
  height = 300,
  width = '100%',
  showLegend = true,
  showTooltip = true,
  showGrid = true,
  xAxisFormat,
  yAxisFormat,
  tooltipFormat,
  isLoading = false,
  emptyMessage = 'No data available',
  className,
  innerRadius = 0.6,
  outerRadius = 80,
  pieLabelKey,
  pieValueKey,
}: ChartContainerProps) {
  // ─── Loading State ──────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        className={cn('flex items-center justify-center', className)}
        style={{ height }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
        <span className="ml-3 text-sm text-[var(--color-muted-foreground)]">
          Loading chart...
        </span>
      </div>
    );
  }

  // ─── Empty State ────────────────────────────────────────────────

  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center text-sm text-[var(--color-muted-foreground)]',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  // ─── Formatters ─────────────────────────────────────────────────

  const xTickFormatter = xAxisFormat ? getTickFormatter(xAxisFormat) : undefined;
  const yTickFormatter = yAxisFormat ? getTickFormatter(yAxisFormat) : undefined;
  const tooltipFormatter = tooltipFormat
    ? (value: number) => getTickFormatter(tooltipFormat)(value)
    : undefined;

  // ─── Visible series ─────────────────────────────────────────────

  const visibleSeries = series.filter((s) => !s.hidden);

  // ─── Pie / Donut Chart ──────────────────────────────────────────

  if (type === 'pie' || type === 'donut') {
    const valueKey = pieValueKey ?? visibleSeries[0]?.dataKey ?? 'value';
    const labelKey = pieLabelKey ?? xAxisKey;
    const computedInnerRadius = type === 'donut' ? outerRadius * innerRadius : 0;

    return (
      <div className={cn('w-full', className)} style={{ height }}>
        <ResponsiveContainer width={width} height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey={valueKey}
              nameKey={labelKey}
              cx="50%"
              cy="50%"
              innerRadius={computedInnerRadius}
              outerRadius={outerRadius}
              paddingAngle={type === 'donut' ? 2 : 0}
              animationDuration={chartTheme.animationDuration}
              label={({ name, percent }) =>
                `${name} (${(percent * 100).toFixed(0)}%)`
              }
              labelLine
            >
              {data.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={visibleSeries[0]?.color ?? getChartColor(index)}
                  stroke="none"
                />
              ))}
            </Pie>
            {showTooltip && <Tooltip {...defaultTooltipProps} />}
            {showLegend && <Legend {...defaultLegendProps} />}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // ─── Cartesian Charts (Line, Bar, Area, Composed) ──────────────

  // Determine wrapper chart component
  const ChartWrapper = getCartesianWrapper(type);

  return (
    <div className={cn('w-full', className)} style={{ height }}>
      <ResponsiveContainer width={width} height={height}>
        <ChartWrapper
          data={data}
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          {showGrid && <CartesianGrid {...defaultCartesianGridProps} />}

          <XAxis
            dataKey={xAxisKey}
            {...defaultXAxisProps}
            tickFormatter={xTickFormatter}
          />

          <YAxis
            {...defaultYAxisProps}
            tickFormatter={yTickFormatter}
          />

          {showTooltip && (
            <Tooltip
              {...defaultTooltipProps}
              formatter={tooltipFormatter}
            />
          )}

          {showLegend && <Legend {...defaultLegendProps} />}

          {visibleSeries.map((s, idx) => {
            const color = s.color ?? getChartColor(idx);
            const seriesType =
              type === 'composed' ? s.chartType ?? 'bar' : type;

            return renderSeries(seriesType, s, color, idx);
          })}
        </ChartWrapper>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function getCartesianWrapper(type: ChartType): React.ComponentType<any> {
  switch (type) {
    case 'line':
      return LineChart;
    case 'bar':
      return BarChart;
    case 'area':
      return AreaChart;
    case 'composed':
      return ComposedChart;
    default:
      return BarChart;
  }
}

function renderSeries(
  seriesType: string,
  s: ChartSeries,
  color: string,
  idx: number,
): React.ReactNode {
  const key = `series-${s.dataKey}-${idx}`;

  switch (seriesType) {
    case 'line':
      return (
        <Line
          key={key}
          type="monotone"
          dataKey={s.dataKey}
          name={s.name ?? s.dataKey}
          stroke={color}
          strokeWidth={chartTheme.strokeWidth}
          dot={{ r: chartTheme.dotRadius, fill: color }}
          activeDot={{ r: chartTheme.activeDotRadius }}
          animationDuration={chartTheme.animationDuration}
          stackId={s.stackId}
        />
      );
    case 'bar':
      return (
        <Bar
          key={key}
          dataKey={s.dataKey}
          name={s.name ?? s.dataKey}
          fill={color}
          radius={[4, 4, 0, 0]}
          animationDuration={chartTheme.animationDuration}
          stackId={s.stackId}
        />
      );
    case 'area':
      return (
        <Area
          key={key}
          type="monotone"
          dataKey={s.dataKey}
          name={s.name ?? s.dataKey}
          stroke={color}
          fill={color}
          fillOpacity={0.15}
          strokeWidth={chartTheme.strokeWidth}
          animationDuration={chartTheme.animationDuration}
          stackId={s.stackId}
        />
      );
    default:
      return (
        <Bar
          key={key}
          dataKey={s.dataKey}
          name={s.name ?? s.dataKey}
          fill={color}
          animationDuration={chartTheme.animationDuration}
          stackId={s.stackId}
        />
      );
  }
}
